import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useSipPhone } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ImageLightbox from "@/components/ImageLightbox";
import { 
  FileText, 
  Search, 
  AlertTriangle, 
  MapPin, 
  Phone, 
  Clock,
  User,
  Users,
  Eye,
  RefreshCw,
  Image as ImageIcon,
  Shield,
  Car,
  Radio,
  Calendar,
  ChevronLeft,
  X,
  UserCircle,
  Play,
  PhoneOutgoing
} from "lucide-react";
import { format } from "date-fns";

// Get base URL for media files
const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const baseUrl = API.replace('/api', '');
  if (url.startsWith('/')) return `${baseUrl}${url}`;
  return `${baseUrl}/uploads/${url}`;
};

// Format time helper
const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

export default function CallHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handlePhoneClick } = useSipPhone() || {};
  const [incidents, setIncidents] = useState([]);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxMediaType, setLightboxMediaType] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [profiles, setProfiles] = useState([]);
  const [selectedIncidentMedia, setSelectedIncidentMedia] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);

  // Fetch incidents and profiles
  const fetchIncidents = async () => {
    try {
      const [incidentsRes, profilesRes] = await Promise.all([
        axios.get(`${API}/incidents`, { withCredentials: true }),
        axios.get(`${API}/perpetrator-profiles`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      // Only show incidents where show_in_call_history is explicitly true
      const callIncidents = incidentsRes.data.filter(inc => 
        inc.show_in_call_history === true
      );
      
      setIncidents(callIncidents);
      setProfiles(profilesRes.data || []);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch incidents:", error);
      toast.error("Failed to load call history");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncidents();
  }, []);

  // Fetch media when an incident is selected (media is excluded from list query for performance)
  useEffect(() => {
    if (selectedIncident?.incident_id) {
      setLoadingMedia(true);
      axios.get(`${API}/incidents/${selectedIncident.incident_id}/media`, { withCredentials: true })
        .then(res => {
          setSelectedIncidentMedia(res.data.media_files || []);
        })
        .catch(err => {
          console.error("Failed to fetch media:", err);
          setSelectedIncidentMedia([]);
        })
        .finally(() => {
          setLoadingMedia(false);
        });
    } else {
      setSelectedIncidentMedia([]);
    }
  }, [selectedIncident?.incident_id]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchIncidents();
    setIsRefreshing(false);
    toast.success("Call history refreshed");
  };

  const filteredIncidents = incidents.filter(incident => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      incident.incident_number?.toLowerCase().includes(search) ||
      incident.incident_id?.toLowerCase().includes(search) ||
      incident.location?.toLowerCase().includes(search) ||
      incident.address?.toLowerCase().includes(search) ||
      incident.headline?.toLowerCase().includes(search) ||
      incident.incident_type?.toLowerCase().includes(search) ||
      incident.created_by_name?.toLowerCase().includes(search)
    );
  });

  const sortedIncidents = [...filteredIncidents].sort((a, b) => {
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const getStatusBadge = (status) => {
    const colors = {
      live_call: "bg-red-600",
      log: "bg-blue-600",
      report: "bg-purple-600",
      investigating: "bg-amber-600",
      arrest: "bg-green-600",
      closed: "bg-slate-600"
    };
    return <Badge className={colors[status] || "bg-slate-600"}>{status?.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col p-4 gap-4">
      {/* Header */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-5 h-5 text-blue-500 shrink-0" />
              <CardTitle className="text-base sm:text-xl uppercase tracking-wider truncate">
                Call History
              </CardTitle>
              <Badge variant="outline" className="shrink-0">
                {sortedIncidents.length}
              </Badge>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="shrink-0"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Incident List */}
        <div className={`w-full lg:w-1/3 min-w-0 bg-slate-900 rounded-lg border border-slate-800 overflow-hidden flex flex-col ${selectedIncident ? 'hidden lg:flex' : ''}`}>
          <div className="p-3 border-b border-slate-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search by ID, location, type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700"
              />
            </div>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-2 space-y-2">
              {sortedIncidents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No call history found</p>
                </div>
              ) : (
                sortedIncidents.map((incident) => (
                  <Card
                    key={incident.incident_id}
                    className={`cursor-pointer transition-colors ${
                      selectedIncident?.incident_id === incident.incident_id
                        ? "bg-blue-900/30 border-blue-500"
                        : "bg-slate-800 border-slate-700 hover:bg-slate-700"
                    }`}
                    onClick={async () => {
                      setSelectedIncident(incident);
                      setActiveTab("details");
                      // Fetch full incident data
                      try {
                        const res = await axios.get(`${API}/incidents/${incident.incident_id}`, { withCredentials: true });
                        setSelectedIncident(res.data);
                      } catch (e) { /* use list data as fallback */ }
                    }}
                  >
                    <CardContent className="p-3">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-mono text-blue-400">
                                {incident.incident_id}
                              </span>
                              {incident.priority === "critical" && (
                                <Badge className="text-xs bg-red-600">CRITICAL</Badge>
                              )}
                              {incident.priority === "high" && (
                                <Badge className="text-xs bg-orange-600">HIGH</Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium mt-1 line-clamp-1">{incident.headline || incident.incident_type}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {formatTime(incident.date_time || incident.created_at)}
                            </p>
                          </div>
                          {getStatusBadge(incident.status)}
                        </div>
                        {incident.address && (
                          <p className="text-xs text-slate-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {incident.address}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Incident Detail - Read Only */}
        <div className={`w-full lg:w-2/3 min-w-0 bg-slate-900 rounded-lg border border-slate-800 overflow-hidden ${selectedIncident ? 'block' : 'hidden lg:block'}`}>
          {selectedIncident ? (
            <div className="h-full flex flex-col">
              {/* Detail Header */}
              <div className="p-3 border-b border-slate-800 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="lg:hidden shrink-0 px-1"
                    onClick={() => setSelectedIncident(null)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold font-mono shrink-0">{selectedIncident.incident_id}</span>
                      {getStatusBadge(selectedIncident.status)}
                      <Badge variant="outline" className="text-[10px] shrink-0">READ ONLY</Badge>
                    </div>
                    <p className="text-xs text-slate-400 truncate">{selectedIncident.headline}</p>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                <div className="overflow-x-auto no-scrollbar mx-4 mt-2">
                  <TabsList className="bg-slate-800 w-fit">
                    <TabsTrigger value="details" className="text-xs">Details</TabsTrigger>
                    <TabsTrigger value="cvcb" className="text-xs">CV/CB</TabsTrigger>
                    <TabsTrigger value="perps" className="text-xs">Perps</TabsTrigger>
                    <TabsTrigger value="profiles" className="text-xs">Profiles</TabsTrigger>
                    <TabsTrigger value="arrests" className="text-xs">Arrests</TabsTrigger>
                    <TabsTrigger value="units" className="text-xs">Units</TabsTrigger>
                    <TabsTrigger value="updates" className="text-xs">Updates</TabsTrigger>
                    <TabsTrigger value="notes" className="text-xs">Notes</TabsTrigger>
                    <TabsTrigger value="media" className="text-xs">Media</TabsTrigger>
                  </TabsList>
                </div>

                <ScrollArea className="flex-1 p-4">
                  {/* Details Tab */}
                  <TabsContent value="details" className="mt-0 space-y-4">
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-blue-500" />Incident Information</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                        <div><p className="text-slate-500 text-xs">Type</p><p>{selectedIncident.incident_type || "—"}</p></div>
                        {selectedIncident.secondary_incident_type && <div><p className="text-slate-500 text-xs">Secondary</p><p>{selectedIncident.secondary_incident_type}</p></div>}
                        <div><p className="text-slate-500 text-xs">Priority</p><p className="capitalize">{selectedIncident.priority || "—"}</p></div>
                        <div><p className="text-slate-500 text-xs">Status</p><p className="capitalize">{selectedIncident.status || "—"}</p></div>
                        <div><p className="text-slate-500 text-xs">Confirmation</p><p className="capitalize">{selectedIncident.confirmation_status || "No Confirm"}</p></div>
                        <div><p className="text-slate-500 text-xs">Report Type</p><p>{selectedIncident.report_type || "—"}</p></div>
                        <div><p className="text-slate-500 text-xs">Date/Time</p><p>{formatTime(selectedIncident.date_time)}</p></div>
                        {selectedIncident.call_final && <div><p className="text-slate-500 text-xs">Call Final</p><Badge className="bg-green-600 text-white text-xs">{selectedIncident.call_final}</Badge></div>}
                        {selectedIncident.from_live_call_id && <div><p className="text-slate-500 text-xs">From Live Call</p><p className="font-mono text-xs text-blue-400">{selectedIncident.from_live_call_id}</p></div>}
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-green-500" />Location</CardTitle></CardHeader>
                      <CardContent className="pt-0 space-y-2 text-sm">
                        <p>{selectedIncident.location_title && <span className="text-amber-400 text-xs font-semibold uppercase mr-1">({selectedIncident.location_title})</span>}{selectedIncident.address || "—"}</p>
                        {selectedIncident.cross_streets && <p className="text-emerald-400">✕ {selectedIncident.cross_streets}</p>}
                        {selectedIncident.location_notes && <p className="text-slate-400 italic">{selectedIncident.location_notes}</p>}
                        {selectedIncident.additional_locations?.map((loc, idx) => (
                          <div key={idx} className="p-2 bg-blue-950/30 border border-blue-800/30 rounded mt-1">
                            {loc.title && <p className="text-[10px] text-blue-300 font-semibold uppercase">{loc.title}</p>}
                            <p className="text-sm">{loc.location}</p>
                            {loc.cross_streets && <p className="text-xs text-emerald-400">✕ {loc.cross_streets}</p>}
                            {loc.location_notes && <p className="text-xs text-slate-400 italic">{loc.location_notes}</p>}
                            {loc.direction_of_flight && <p className="text-xs text-amber-400">→ {loc.direction_of_flight}</p>}
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                    {/* Flags */}
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />Flags</CardTitle></CardHeader>
                      <CardContent className="pt-0">
                        <div className="flex flex-wrap gap-2">
                          {selectedIncident.is_pr && <Badge className="bg-amber-900">PR</Badge>}
                          {selectedIncident.is_arrest && <Badge className="bg-green-900">Arrest</Badge>}
                          {selectedIncident.transmitted_10_85 && <Badge className="bg-red-900">10-85</Badge>}
                          {selectedIncident.transmitted_10_13 && <Badge className="bg-red-900">10-13</Badge>}
                          {selectedIncident.weapon_displayed && <Badge className="bg-amber-900">Weapon</Badge>}
                          {selectedIncident.injuries_reported && <Badge className="bg-orange-900">Injuries</Badge>}
                          {selectedIncident.pd_activated && <Badge className="bg-blue-900">PD Activated</Badge>}
                          {selectedIncident.ems_required && <Badge className="bg-purple-900">EMS</Badge>}
                          {selectedIncident.is_hotline_call && <Badge className="bg-blue-900">Hotline</Badge>}
                          {selectedIncident.is_hate_crime && <Badge className="bg-red-900">Hate Crime</Badge>}
                        </div>
                      </CardContent>
                    </Card>
                    {/* Related Incidents */}
                    {selectedIncident.related_incidents?.length > 0 && (
                      <Card className="bg-slate-800 border-slate-700">
                        <CardHeader className="py-3"><CardTitle className="text-sm">Related Incidents ({selectedIncident.related_incidents.length})</CardTitle></CardHeader>
                        <CardContent className="pt-0 space-y-1">
                          {selectedIncident.related_incidents.map((rel) => (
                            <div key={rel.incident_id} className="p-2 bg-slate-900 rounded text-sm cursor-pointer hover:bg-slate-800" onClick={() => navigate(`/incidents/${rel.incident_id}`)}>
                              <span className="font-mono text-blue-400">{rel.incident_id}</span>
                              {rel.headline && <span className="text-slate-300 ml-2">{rel.headline}</span>}
                              {rel.note && <span className="text-xs text-slate-500 ml-2">— {rel.note}</span>}
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}
                    <div className="text-xs text-slate-500 space-y-1">
                      <p>Created by: {selectedIncident.created_by_name} on {formatTime(selectedIncident.created_at)}</p>
                    </div>
                  </TabsContent>

                  {/* CV/CB Tab */}
                  <TabsContent value="cvcb" className="mt-0 space-y-4">
                    {selectedIncident.callback_numbers?.length > 0 && (
                      <Card className="bg-slate-800 border-slate-700">
                        <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Phone className="w-4 h-4 text-blue-500" />Callbacks ({selectedIncident.callback_numbers.length})</CardTitle></CardHeader>
                        <CardContent className="pt-0 space-y-2">
                          {selectedIncident.callback_numbers.map((cb, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-slate-900 rounded">
                              <span className="text-blue-400">{cb.number}</span>
                              {(cb.caller_type || cb.type) && <Badge variant="outline" className="text-[10px]">{(cb.caller_type || cb.type) === 'cv' ? 'CV' : '3rd Party'}</Badge>}
                              {cb.has_visual && <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400">VISUAL</Badge>}
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><User className="w-4 h-4 text-blue-500" />Victims ({selectedIncident.victims?.length || 0})</CardTitle></CardHeader>
                      <CardContent className="pt-0">
                        {selectedIncident.victims?.length > 0 ? (
                          <div className="space-y-2">{selectedIncident.victims.map((v, idx) => (
                            <div key={idx} className="p-2 bg-slate-900 rounded text-sm space-y-1">
                              <p className="font-medium">{v.name || `Victim ${idx + 1}`}</p>
                              {v.phone1 && <p className="text-slate-400 text-xs">Phone: {v.phone1}</p>}
                              {v.phone2 && <p className="text-slate-400 text-xs">Phone 2: {v.phone2}</p>}
                              {v.address && <p className="text-slate-400 text-xs">Address: {v.address}</p>}
                              {v.email && <p className="text-slate-400 text-xs">Email: {v.email}</p>}
                              {v.has_injuries && <Badge className="text-[10px] bg-orange-600">Injuries</Badge>}
                              {v.injuries_description && <p className="text-xs text-orange-400">{v.injuries_description}</p>}
                              {v.statement && <p className="text-xs text-slate-400 mt-1 whitespace-pre-wrap">{v.statement}</p>}
                              {v.property_stolen && <p className="text-xs text-slate-400">Property: {v.property_stolen}</p>}
                            </div>
                          ))}</div>
                        ) : <p className="text-slate-500 text-sm">No victims</p>}
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Eye className="w-4 h-4 text-purple-500" />Witnesses ({selectedIncident.witnesses?.length || 0})</CardTitle></CardHeader>
                      <CardContent className="pt-0">
                        {selectedIncident.witnesses?.length > 0 ? (
                          <div className="space-y-2">{selectedIncident.witnesses.map((w, idx) => (
                            <div key={idx} className="p-2 bg-slate-900 rounded text-sm space-y-1">
                              <p className="font-medium">{w.name || `Witness ${idx + 1}`}</p>
                              {(w.contact_info || w.phone1) && <p className="text-slate-400 text-xs">Contact: {w.contact_info || w.phone1}</p>}
                              {w.location && <p className="text-slate-400 text-xs">Location: {w.location}</p>}
                              {w.statement && <p className="text-xs text-slate-400 mt-1 whitespace-pre-wrap">{w.statement}</p>}
                            </div>
                          ))}</div>
                        ) : <p className="text-slate-500 text-sm">No witnesses</p>}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Perps Tab */}
                  <TabsContent value="perps" className="mt-0 space-y-4">
                    {selectedIncident.perp_descriptions?.length > 0 ? (
                      <div className="space-y-3">{selectedIncident.perp_descriptions.map((p, idx) => (
                        <Card key={idx} className="bg-slate-800 border-slate-700">
                          <CardContent className="p-3 text-sm">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline">#{idx + 1}</Badge>
                              <span className="font-medium">{p.name || `Perp #${idx + 1}`}</span>
                              {p.was_arrested && <Badge className="bg-green-600 text-[10px]">ARRESTED</Badge>}
                              {p.has_weapon && <Badge className="bg-red-600 text-[10px]">ARMED</Badge>}
                              {p.linked_profile_name && <Badge className="bg-purple-600 text-[10px]">{p.linked_profile_name}</Badge>}
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-slate-400">
                              {p.sex && <div><span className="text-slate-500">Sex:</span> {p.sex}</div>}
                              {p.race && <div><span className="text-slate-500">Race:</span> {p.race}</div>}
                              {p.age && <div><span className="text-slate-500">Age:</span> {p.age}</div>}
                              {p.height && <div><span className="text-slate-500">Height:</span> {p.height}</div>}
                              {p.weight && <div><span className="text-slate-500">Weight:</span> {p.weight}</div>}
                              {p.build && <div><span className="text-slate-500">Build:</span> {p.build}</div>}
                              {p.complexion && <div><span className="text-slate-500">Complexion:</span> {p.complexion}</div>}
                              {p.hair_color_cut && <div><span className="text-slate-500">Hair:</span> {p.hair_color_cut}</div>}
                              {p.eye_color && <div><span className="text-slate-500">Eyes:</span> {p.eye_color}</div>}
                              {p.hat_color_style && <div><span className="text-slate-500">Hat:</span> {p.hat_color_style}</div>}
                              {p.shirt && <div><span className="text-slate-500">Shirt:</span> {p.shirt}</div>}
                              {p.pants && <div><span className="text-slate-500">Pants:</span> {p.pants}</div>}
                              {p.jacket_coat && <div><span className="text-slate-500">Jacket:</span> {p.jacket_coat}</div>}
                              {p.shoes && <div><span className="text-slate-500">Shoes:</span> {p.shoes}</div>}
                              {p.clothing && <div className="col-span-2"><span className="text-slate-500">Clothing:</span> {p.clothing}</div>}
                              {p.visible_marks && <div className="col-span-2"><span className="text-slate-500">Marks:</span> {p.visible_marks}</div>}
                              {p.vehicle_description && <div className="col-span-2"><span className="text-slate-500">Vehicle:</span> {p.vehicle_description}</div>}
                              {p.license_plate && <div><span className="text-slate-500">Plate:</span> {p.license_plate}</div>}
                              {p.weapon_description && <div className="col-span-2"><span className="text-slate-500">Weapon:</span> {p.weapon_description}</div>}
                              {p.direction_of_flight && <div className="col-span-2"><span className="text-slate-500">Direction:</span> {p.direction_of_flight}</div>}
                              {p.address && <div className="col-span-2"><span className="text-slate-500">Address:</span> {p.address}</div>}
                            </div>
                            {p.notes && <p className="text-xs text-slate-400 mt-2 whitespace-pre-wrap border-t border-slate-700 pt-2">{p.notes}</p>}
                            {p.description && <p className="text-xs text-slate-400 mt-1 whitespace-pre-wrap">{p.description}</p>}
                          </CardContent>
                        </Card>
                      ))}</div>
                    ) : <p className="text-slate-500 text-sm text-center py-8">No perpetrators</p>}
                  </TabsContent>

                  {/* Profiles Tab */}
                  <TabsContent value="profiles" className="mt-0 space-y-4">
                    {selectedIncident.linked_profiles?.length > 0 ? (
                      <div className="space-y-3">{selectedIncident.linked_profiles.map((profileId) => {
                        const profile = profiles.find(p => p.profile_id === profileId);
                        if (!profile) return <div key={profileId} className="text-xs text-slate-500">Profile {profileId} not found</div>;
                        return (
                          <Card key={profileId} className="bg-purple-950/20 border-purple-800/50 cursor-pointer hover:bg-purple-900/30" onClick={() => navigate(`/perpetrators?profile=${profile.profile_id}`)}>
                            <CardContent className="p-3">
                              <div className="flex items-center gap-3">
                                {profile.mugshot_url ? (
                                  <img src={getMediaUrl(profile.mugshot_url)} alt={profile.name} className="w-14 h-14 rounded-full object-cover border-2 border-purple-500" />
                                ) : (
                                  <div className="w-14 h-14 rounded-full bg-slate-700 flex items-center justify-center"><UserCircle className="w-10 h-10 text-slate-500" /></div>
                                )}
                                <div>
                                  <p className="font-medium text-purple-300">{profile.name}</p>
                                  {profile.known_address && <p className="text-xs text-slate-400">{profile.known_address}</p>}
                                  {profile.aliases && <p className="text-xs text-slate-500">AKA: {profile.aliases}</p>}
                                  <div className="flex gap-1 mt-1">
                                    {profile.status === "warrant" && <Badge className="bg-red-600 text-[10px]">WARRANT</Badge>}
                                    {profile.status === "ready_for_arrest" && <Badge className="bg-amber-600 text-[10px]">READY</Badge>}
                                    {profile.status === "arrested" && <Badge className="bg-green-600 text-[10px]">ARRESTED</Badge>}
                                    {profile.arrest_count > 0 && <Badge variant="outline" className="text-[10px]">{profile.arrest_count} arrests</Badge>}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}</div>
                    ) : <p className="text-slate-500 text-sm text-center py-8">No linked profiles</p>}
                  </TabsContent>

                  {/* Arrests Tab */}
                  <TabsContent value="arrests" className="mt-0 space-y-4">
                    {selectedIncident.arrests?.length > 0 ? (
                      <div className="space-y-3">{selectedIncident.arrests.map((a, idx) => (
                        <Card key={a.arrest_id || idx} className="bg-slate-800 border-slate-700">
                          <CardContent className="p-3 text-sm space-y-1">
                            <p className="font-medium">Arrest #{idx + 1} — {a.perp_description_summary || "Unknown"}</p>
                            {a.arrest_date && <p className="text-xs text-slate-400">Date: {formatTime(a.arrest_date)}</p>}
                            {a.linked_profile_id && <p className="text-xs text-purple-400">Linked Profile: {profiles.find(p => p.profile_id === a.linked_profile_id)?.name || a.linked_profile_id}</p>}
                            {a.identification_by?.length > 0 && <p className="text-xs text-slate-400">ID by: {a.identification_by.join(', ')}</p>}
                            {a.notes && <p className="text-xs text-slate-400 whitespace-pre-wrap">{a.notes}</p>}
                          </CardContent>
                        </Card>
                      ))}</div>
                    ) : <p className="text-slate-500 text-sm text-center py-8">No arrests</p>}
                  </TabsContent>

                  {/* Units Tab */}
                  <TabsContent value="units" className="mt-0 space-y-4">
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4 text-green-500" />Units Responded ({selectedIncident.units_responded?.length || 0})</CardTitle></CardHeader>
                      <CardContent className="pt-0">
                        {selectedIncident.units_responded?.length > 0 ? <div className="flex flex-wrap gap-2">{selectedIncident.units_responded.map(u => <Badge key={u} variant="outline">{u}</Badge>)}</div> : <p className="text-slate-500 text-sm">None</p>}
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4 text-blue-500" />Investigating ({selectedIncident.units_investigating?.length || 0})</CardTitle></CardHeader>
                      <CardContent className="pt-0">
                        {selectedIncident.units_investigating?.length > 0 ? <div className="flex flex-wrap gap-2">{selectedIncident.units_investigating.map(u => <Badge key={u} variant="outline" className="border-blue-500">{u}</Badge>)}</div> : <p className="text-slate-500 text-sm">None</p>}
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Radio className="w-4 h-4 text-amber-500" />Dispatcher ({selectedIncident.dispatcher_units?.length || 0})</CardTitle></CardHeader>
                      <CardContent className="pt-0">
                        {selectedIncident.dispatcher_units?.length > 0 ? <div className="flex flex-wrap gap-2">{selectedIncident.dispatcher_units.map(u => <Badge key={u} variant="outline" className="border-amber-500">{u}</Badge>)}</div> : <p className="text-slate-500 text-sm">None</p>}
                      </CardContent>
                    </Card>
                    {selectedIncident.assigned_detective && (
                      <Card className="bg-slate-800 border-slate-700"><CardContent className="p-3"><p className="text-sm"><span className="text-slate-500">Detective:</span> {selectedIncident.assigned_detective}</p></CardContent></Card>
                    )}
                  </TabsContent>

                  {/* Updates Tab */}
                  <TabsContent value="updates" className="mt-0 space-y-4">
                    {selectedIncident.call_final && (
                      <Card className="bg-slate-800 border-slate-700"><CardContent className="p-3"><span className="text-slate-500 text-sm">Call Final:</span> <Badge className="bg-green-600 ml-2">{selectedIncident.call_final}</Badge></CardContent></Card>
                    )}
                    {selectedIncident.live_call_description && (
                      <Card className="bg-slate-800 border-slate-700"><CardHeader className="py-3"><CardTitle className="text-sm">Live Call Description</CardTitle></CardHeader><CardContent className="pt-0"><p className="text-sm whitespace-pre-wrap">{selectedIncident.live_call_description}</p></CardContent></Card>
                    )}
                    {selectedIncident.live_call_notes && (
                      <Card className="bg-slate-800 border-slate-700"><CardHeader className="py-3"><CardTitle className="text-sm">Live Call Summary</CardTitle></CardHeader><CardContent className="pt-0"><p className="text-sm whitespace-pre-wrap text-slate-300">{selectedIncident.live_call_notes}</p></CardContent></Card>
                    )}
                  </TabsContent>

                  {/* Notes Tab */}
                  <TabsContent value="notes" className="mt-0 space-y-4">
                    {selectedIncident.notes?.length > 0 ? (
                      <div className="space-y-2">{selectedIncident.notes.map((note, idx) => {
                        const noteText = typeof note === 'string' ? note : (note?.text || note?.note || String(note));
                        return (
                          <Card key={idx} className="bg-slate-800 border-slate-700">
                            <CardContent className="p-3">
                              {(note?.created_at || note?.created_by || note?.edited_at) && (
                                <div className="flex items-center gap-2 flex-wrap mb-1.5 text-[10px] text-slate-500">
                                  {note.created_by && <span className="text-slate-400 font-medium">{note.created_by}</span>}
                                  {note.created_at && <span className="font-mono">{formatTime(note.created_at)}</span>}
                                  {note.edited_at && <span className="text-amber-500 italic">(edited {formatTime(note.edited_at)}{note.edited_by ? ` by ${note.edited_by}` : ""})</span>}
                                </div>
                              )}
                              <p className="text-sm whitespace-pre-wrap">{noteText}</p>
                            </CardContent>
                          </Card>
                        );
                      })}</div>
                    ) : <p className="text-slate-500 text-sm text-center py-8">No notes</p>}
                  </TabsContent>

                  {/* Media Tab */}
                  <TabsContent value="media" className="mt-0 space-y-4">
                    {loadingMedia ? (
                      <p className="text-slate-500 text-sm text-center py-8">Loading media...</p>
                    ) : selectedIncidentMedia?.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {selectedIncidentMedia.map((media, idx) => {
                          const url = getMediaUrl(media.url || media);
                          const isVideo = url?.includes('video') || media.type?.includes('video');
                          return (
                            <div key={idx} className="relative">
                              {isVideo ? (
                                <div className="relative w-full h-28 bg-slate-900 rounded border border-slate-700 overflow-hidden cursor-pointer" onClick={() => { setLightboxImage(url); setLightboxMediaType('video'); }}>
                                  <video src={url} className="w-full h-full object-cover pointer-events-none" muted playsInline preload="metadata" onLoadedMetadata={(e) => { e.target.currentTime = 1; }} />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/20"><div className="bg-black/50 rounded-full p-1.5"><Play className="w-5 h-5 text-white" /></div></div>
                                </div>
                              ) : (
                                <div className="relative w-full h-28 rounded border border-slate-700 overflow-hidden cursor-pointer hover:opacity-80" onClick={() => { setLightboxImage(url); setLightboxMediaType('image'); }}>
                                  <img src={url} alt={`Media ${idx + 1}`} className="w-full h-full object-cover pointer-events-none" draggable="false" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : <p className="text-slate-500 text-sm text-center py-8">No media files</p>}
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-slate-500">
              <div className="text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Select a call to view details</p>
                <p className="text-sm mt-2">View archived call history (read-only)</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Lightbox */}
      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage}
          alt="Call history media"
          mediaType={lightboxMediaType}
          onClose={() => { setLightboxImage(null); setLightboxMediaType(null); }}
        />
      )}
    </div>
  );
}
